import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';
import petSittingHeroImageDesktop from '../shared-images/Hero_Pet_desktop.jpg';
import petSittingHeroImageMobile from '../shared-images/Hero_Pet_mobile.jpg';
import petSittingHeroImageTablet from '../shared-images/Hero_Pet_tablet.jpg';

const petSittingImages: HeroImageSrcSet = {
  desktop: {
    src: petSittingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: petSittingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: petSittingHeroImageMobile,
    height: 614,
  },
};
const PetSitting: React.FC<Props> = ({
  serviceLinks,
  countryConfig,
  citiesAndNeighborhoods,
  language,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.PET_SITTING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x" heroImageSrcSet={petSittingImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Finally—a pet sitting option that works</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>No need to uproot your pet</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Your sitter will stop by to feed, play with, and snuggle your pet as many times
                    a day as you like. And the best part? Your best friend gets to stay on their
                    home turf.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Pet sitters you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every pet sitter on the site has been reviewed and approved by the Rover team.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your dog's perfect match before you book. Request a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Affordable, personalized pet care</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Worried that pet sitting will be more expensive than kennels? Find out{' '}
                    <Link href="/dog-sitting-rates/" target="_blank">
                      how much
                    </Link>{' '}
                    pet sitters near you typically cost.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>

          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Book drop-in visits</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search Pet Sitters</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Browse profiles of nearby sitters who offer drop-in visits.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Meet In Person</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>All payments are processed through Rover's secured platform.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Thousands of drop-in visits booked. Thousands of happy pets.</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Is your dog happy to nap at home all day—or is your dog actually a cat? Instead of a
                full-time pet sitter, book someone to drop by a few times a day to feed and check on
                your pets. Drop-in pet sitting is great for:
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.List>
              <ServiceTargetAudience.ListItem>
                <Trans>Low-maintenance dogs</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  <Link
                    href={serviceLinks[ServiceLandingEnum.CAT_SITTING]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Cats
                  </Link>{' '}
                  and caged pets
                </Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Help with small household tasks</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  <Link
                    href={serviceLinks[ServiceLandingEnum.LONG_TERM_DOG_BOARDING]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Long-term bookings
                  </Link>
                </Trans>
              </ServiceTargetAudience.ListItem>
            </ServiceTargetAudience.List>
          </ServiceTargetAudience>

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default PetSitting;
